import { effectiveDimensions, effectiveTerminals } from "../effective";
import type { Cable, ComponentDefinition, ComponentNode, SystemNode } from "../types";

type Point = { x: number; y: number };
type Rect = Point & { width: number; height: number };
const GAP = 80;
const CLEARANCE = 32;

/** Place only the additions. Wiring supplies the neighbourhood; existing peers
 * supply its row/column. Zones are coordinate spaces, not occupied bodies, and
 * are never assigned here: their membership also affects electrical calculations.
 * Kept edge-safe because the legacy endpoint runs this on a reconstructed digest. */
export function placeAddedNodes(
  added: ComponentNode[],
  nodes: SystemNode[],
  definitions: Map<string, ComponentDefinition>,
  cables: Cable[],
  options: { fallbackCenter?: Point; boundsByNode?: Map<string, Rect> } = {},
): void {
  const pending = new Map(added.map(node => [node.id, node]));
  const byId = new Map(nodes.map(node => [node.id, node]));
  const sizeOf = (node: ComponentNode) => {
    const def = definitions.get(node.id);
    return def ? effectiveDimensions(def, node) : { width: 120, height: 80 };
  };
  const placed = new Map<string, Rect>();
  for (const node of nodes) {
    if (node.kind === "component" && !pending.has(node.id)) {
      placed.set(node.id, { ...absolute(node, byId), ...sizeOf(node) });
    }
  }

  // Saved routes are preserved by an edit. Unstored routes already avoid the
  // current bodies when rendered. Zone outlines are also worth keeping clear,
  // without treating their entire interiors as occupied space.
  const protectedSegments: Array<[Point, Point]> = [];
  const addPath = (points: Point[]) => {
    for (let i = 1; i < points.length; i++) protectedSegments.push([points[i - 1], points[i]]);
  };
  const endpoint = (id: string, terminalId: string): Point | null => {
    const node = byId.get(id);
    const def = definitions.get(id);
    const rect = placed.get(id);
    if (node?.kind !== "component" || !def || !rect) return null;
    if (def.renderVariant === "junction") return centre(rect);
    const terminal = effectiveTerminals(def, node).find(t => t.id === terminalId);
    if (!terminal) return null;
    const { side, offset } = terminal;
    return {
      x: rect.x + rect.width * (side === "left" ? 0 : side === "right" ? 1 : offset),
      y: rect.y + rect.height * (side === "top" ? 0 : side === "bottom" ? 1 : offset),
    };
  };
  for (const cable of cables) {
    if (!cable.waypoints?.length) continue;
    const from = endpoint(cable.fromNodeId, cable.fromTerminalId);
    const to = endpoint(cable.toNodeId, cable.toTerminalId);
    if (from && to) addPath([from, ...cable.waypoints, to]);
  }
  for (const node of nodes) {
    if (node.kind !== "zone") continue;
    const { x, y } = absolute(node, byId);
    addPath([{ x, y }, { x: x + node.width, y }, { x: x + node.width, y: y + node.height }, { x, y: y + node.height }, { x, y }]);
  }

  // One neighbour per component, regardless of how many rails connect it.
  const neighbours = new Map<string, Set<string>>();
  for (const cable of cables) {
    for (const [id, other] of [[cable.fromNodeId, cable.toNodeId], [cable.toNodeId, cable.fromNodeId]]) {
      if (!neighbours.has(id)) neighbours.set(id, new Set());
      neighbours.get(id)!.add(other);
    }
  }
  const knownNeighbours = (id: string) => [...(neighbours.get(id) ?? [])].filter(other => placed.has(other));

  while (pending.size) {
    // Work outwards from the drawing, including additions already placed. A
    // model may list the panels before the controller connecting them to it.
    const node = [...pending.values()].sort((a, b) =>
      knownNeighbours(b.id).length - knownNeighbours(a.id).length || compareIds(a.id, b.id),
    )[0];
    const size = sizeOf(node);
    const anchorIds = knownNeighbours(node.id);
    const anchors = anchorIds.map(id => placed.get(id)!);
    const obstacles = [...placed.values()];
    const bounds = boundsOf(obstacles);
    const target = anchors.length ? average(anchors.map(centre)) :
      obstacles.length ? centre(bounds) : options.fallbackCenter ?? centre(bounds);
    const container = options.boundsByNode?.get(node.id);
    const def = definitions.get(node.id);

    // Prefer peers on the same circuit. A distant, unrelated load should not
    // pull a new branch away from its own distribution panel.
    const peers = [...placed.entries()].filter(([id]) => sameRole(def, definitions.get(id)));
    const sharedCount = (id: string) => anchorIds.filter(anchor => neighbours.get(id)?.has(anchor)).length;
    const mostShared = Math.max(0, ...peers.map(([id]) => sharedCount(id)));
    const peerRects = peers
      .filter(([id, rect]) => mostShared > 0 ? sharedCount(id) === mostShared :
        !anchors.length || distance(centre(rect), target) < 800)
      .map(([, rect]) => rect);
    const focus = !anchors.length && peerRects.length ? average(peerRects.map(centre)) : target;
    const lane = peerLane(peerRects, anchors);

    // Without peers, the existing terminal faces provide a useful direction
    // (for example, panels above a controller's PV inputs).
    const outward: Point[] = [];
    if (!peerRects.length) {
      for (const cable of cables) {
        const otherId = cable.fromNodeId === node.id ? cable.toNodeId :
          cable.toNodeId === node.id ? cable.fromNodeId : null;
        if (!otherId || !placed.has(otherId)) continue;
        const other = byId.get(otherId);
        const otherDef = definitions.get(otherId);
        if (other?.kind !== "component" || !otherDef) continue;
        const terminalId = cable.fromNodeId === node.id ? cable.toTerminalId : cable.fromTerminalId;
        const terminal = effectiveTerminals(otherDef, other).find(t => t.id === terminalId);
        if (!terminal) continue;
        const rect = placed.get(otherId)!;
        const point = centre(rect);
        if (terminal.side === "left") point.x = rect.x - GAP - size.width / 2;
        if (terminal.side === "right") point.x = rect.x + rect.width + GAP + size.width / 2;
        if (terminal.side === "top") point.y = rect.y - GAP - size.height / 2;
        if (terminal.side === "bottom") point.y = rect.y + rect.height + GAP + size.height / 2;
        outward.push(point);
      }
    }

    const candidates: Rect[] = [];
    const seen = new Set<string>();
    const propose = (x: number, y: number) => {
      const rect = {
        x: Math.round(container ? Math.max(container.x, x) : x),
        y: Math.round(container ? Math.max(container.y, y) : y),
        ...size,
      };
      const key = `${rect.x}:${rect.y}`;
      if (!seen.has(key)) { seen.add(key); candidates.push(rect); }
    };
    const at = (point: Point) => propose(point.x - size.width / 2, point.y - size.height / 2);
    at(focus);
    outward.forEach(at);
    if (container) {
      propose(container.x, container.y);
      propose(container.x + container.width - size.width, container.y);
      propose(container.x, container.y + container.height - size.height);
    }
    // Search gaps throughout the local cluster, not just a downward scan in
    // one column. Limit seeds, but check every body for collisions.
    const nearby = [...obstacles].sort((a, b) => distance(centre(a), focus) - distance(centre(b), focus)).slice(0, 32);
    for (const rect of [...nearby, bounds]) {
      const mid = centre(rect);
      for (const x of [mid.x - size.width / 2, focus.x - size.width / 2]) {
        propose(x, rect.y + rect.height + GAP);
        propose(x, rect.y - size.height - GAP);
      }
      for (const y of [mid.y - size.height / 2, focus.y - size.height / 2]) {
        propose(rect.x + rect.width + GAP, y);
        propose(rect.x - size.width - GAP, y);
      }
    }
    for (let y = -4; y <= 4; y++) {
      for (let x = -4; x <= 4; x++) at({ x: focus.x + x * GAP, y: focus.y + y * GAP });
    }

    const score = (rect: Rect) => {
      const point = centre(rect);
      const wiring = anchors.length ? meanDistance(point, anchors.map(centre)) : distance(point, focus) * (peerRects.length ? 0.1 : 1);
      const peerDistance = peerRects.length ? Math.min(...peerRects.map(peer => distance(point, centre(peer)))) : 0;
      const alignment = lane ? Math.min(...peerRects.map(peer => Math.abs(point[lane] - centre(peer)[lane]))) : 0;
      const direction = outward.length ? meanDistance(point, outward) * 0.4 : 0;
      const growth = Math.max(0, bounds.x - rect.x) + Math.max(0, bounds.y - rect.y) +
        Math.max(0, rect.x + rect.width - bounds.x - bounds.width) +
        Math.max(0, rect.y + rect.height - bounds.y - bounds.height);
      const crossings = protectedSegments.filter(([a, b]) => crossesBody(a, b, rect)).length;
      // Keep members inside their zone when space permits. Overflow can only
      // grow its right/bottom edges; fixed components never shift with it.
      const overflow = container ? Math.max(0, rect.x + rect.width - container.x - container.width) +
        Math.max(0, rect.y + rect.height - container.y - container.height) : 0;
      return overflow * 100 + wiring + peerDistance * 0.8 + alignment * 2 + direction + growth * 0.15 + crossings * 600;
    };
    // The four candidates outside the full bounds always provide a clear
    // fallback, even for huge or coincident bodies; no retry limit can overlap.
    let best: Rect | undefined;
    let bestScore = Infinity;
    for (const candidate of candidates) {
      if (obstacles.some(rect => overlaps(rect, candidate))) continue;
      const cost = score(candidate);
      if (cost < bestScore) { best = candidate; bestScore = cost; }
    }
    if (!best) throw new Error("No clear component placement outside drawing bounds");
    node.position = { x: best.x, y: best.y };
    placed.set(node.id, best);
    pending.delete(node.id);
  }
}

function sameRole(a?: ComponentDefinition, b?: ComponentDefinition): boolean {
  if (!a || !b || a.category !== b.category) return false;
  // Positive/return bars and different distribution devices have different
  // places in a circuit despite sharing a catalog category.
  return a.category !== "distribution" || a.id === b.id;
}

function peerLane(peers: Rect[], anchors: Rect[]): "x" | "y" | null {
  if (!peers.length) return null;
  const spread = (rects: Rect[]) => {
    const points = rects.map(centre);
    return {
      x: Math.max(...points.map(p => p.x)) - Math.min(...points.map(p => p.x)),
      y: Math.max(...points.map(p => p.y)) - Math.min(...points.map(p => p.y)),
    };
  };
  if (peers.length > 1) {
    const span = spread(peers);
    if (span.x || span.y) return span.x <= span.y ? "x" : "y";
  }
  if (!anchors.length) return null;
  const span = spread(anchors);
  const from = average(anchors.map(centre));
  const to = centre(peers[0]);
  // A horizontal feed/return arrangement implies a column of loads, and vice versa.
  return Math.max(span.x, Math.abs(from.x - to.x)) >= Math.max(span.y, Math.abs(from.y - to.y)) ? "x" : "y";
}

function compareIds(a: string, b: string): number { return a < b ? -1 : a > b ? 1 : 0; }
function centre(rect: Rect): Point { return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 }; }
function distance(a: Point, b: Point): number { return Math.abs(a.x - b.x) + Math.abs(a.y - b.y); }
function average(points: Point[]): Point {
  return { x: points.reduce((sum, p) => sum + p.x, 0) / points.length, y: points.reduce((sum, p) => sum + p.y, 0) / points.length };
}
function meanDistance(point: Point, others: Point[]): number {
  return others.reduce((sum, other) => sum + distance(point, other), 0) / others.length;
}
function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width + CLEARANCE && a.x + a.width + CLEARANCE > b.x &&
    a.y < b.y + b.height + CLEARANCE && a.y + a.height + CLEARANCE > b.y;
}
/** Clip a segment against the body's interior; handles diagonal saved bends too. */
function crossesBody(a: Point, b: Point, rect: Rect): boolean {
  let enter = 0;
  let leave = 1;
  for (const axis of ["x", "y"] as const) {
    const low = rect[axis];
    const high = low + (axis === "x" ? rect.width : rect.height);
    const delta = b[axis] - a[axis];
    if (delta === 0) {
      if (a[axis] <= low || a[axis] >= high) return false;
    } else {
      const first = (low - a[axis]) / delta;
      const last = (high - a[axis]) / delta;
      enter = Math.max(enter, Math.min(first, last));
      leave = Math.min(leave, Math.max(first, last));
    }
  }
  return enter < leave;
}
function boundsOf(rects: Rect[]): Rect {
  if (!rects.length) return { x: 0, y: 0, width: 0, height: 0 };
  const x = Math.min(...rects.map(rect => rect.x));
  const y = Math.min(...rects.map(rect => rect.y));
  return { x, y, width: Math.max(...rects.map(rect => rect.x + rect.width)) - x, height: Math.max(...rects.map(rect => rect.y + rect.height)) - y };
}

function absolute(node: SystemNode, byId: Map<string, SystemNode>): Point {
  let { x, y } = node.position;
  let current = node;
  const seen = new Set([node.id]);
  while (current.parentId && !seen.has(current.parentId)) {
    seen.add(current.parentId);
    const parent = byId.get(current.parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    current = parent;
  }
  return { x, y };
}
